import httpRequest from '@/api/request'
import {
  IResAccountDetailType,
  IResAccountInfoType,
  IResBrowserAssetsType,
  IResT7LockType,
  IPledgeListParams,
  IResAgsPledgeListItem,
  IResAgdPledgeListItem,
  IResAgePledgeListItem,
  IResCheckIn,
  IResParams,
  IResTransactionRecord,
  IResModuleTransactionRecord,
  IResAccountRewards,
  CheckinRecords_Data,
} from '@/api/account/interface.ts'
import { IPageBaseParamsType } from '@/api/public.interface.ts'
import adminRequest from '@/api/adminRequest.ts'
import { getFormatTimeZone } from '@/utils'

/**
 * @description 获取账户详情
 * @param {string}  address
 * @param {string} denom 默认值是 umec
 * @return {Promise<IResAccountDetailType>}
 * */
export const getAccountDetail = (
  address: string,
  denom: string = 'umec',
): Promise<IResAccountDetailType> => {
  return httpRequest.get(`/api/account/${address}`, {
    params: {
      denom,
    },
  })
}

/**
 * @description 获取账户信息
 * @param {string}  address
 * @return {Promise<IResAccountDetailType>}
 * */
export const getAccountInfo = (address: string): Promise<IResAccountInfoType> => {
  return httpRequest.get(`/api/rest/account/detail`, {
    params: {
      address,
    },
  })
}

/**
 * @description 账户T+7锁仓列表
 * @param {string}  address
 * @param {IPageBaseParamsType} params
 * @param {string | null} gte_height
 * @return {Promise<IResTxListItemType>}
 * */
export const getT7LockList = (
  address: string,
  params: IPageBaseParamsType,
): Promise<IResT7LockType> => {
  return httpRequest.post(`/api/rest/unbond`, {
    address,
    unbound_after_time: getFormatTimeZone(new Date().getTime()).currentUtc0,
    ...params,
  })
}
/**
 * @description 查询浏览器账户详情
 * @param address
 */
export const getBrowserAccountDetail = (address: string): Promise<IResBrowserAssetsType> => {
  return adminRequest.get(`/api/browser/account/detail`, {
    params: {
      address,
    },
  })
}

/**
 * @description 查询账户ags质押列表
 * @param address
 */
export const getAgsPledgeList = (
  params: IPledgeListParams,
): Promise<{ agsDelegation: IResAgsPledgeListItem[]; total: number }> => {
  return httpRequest.get(`/api/account/ags_delegate`, {
    params,
  })
}

/**
 * @description 查询账户agd质押列表
 * @param address
 */
export const getAgdPledgeList = (
  params: IPledgeListParams,
): Promise<{ agdDelegation: IResAgdPledgeListItem[]; total: number }> => {
  return httpRequest.get(`/api/account/agd_delegate`, {
    params,
  })
}

/**
 * @description 查询账户agd质押列表
 * @param address
 */
export const getAgePledgeList = (
  params: IPledgeListParams,
): Promise<{ ageDelegation: IResAgePledgeListItem[]; total: number }> => {
  return httpRequest.get(`/api/account/age_delegate`, {
    params,
  })
}

/**
 * @description 查询账户agd质押列表
 * @param address
 */
export const getCheckInCount = (address: string): Promise<IResCheckIn> => {
  return httpRequest.get(`/api/rest/checkin/count/${address}`)
}

/**
 * @description 查询账户交易列表
 * @param address
 */
export const getAccountTransactionRecord = ({
  ...params
}: IResParams): Promise<{
  transaction: IResTransactionRecord[]
  next_key: string
  total_tx: number
  group_data: number
  offset_key: string
  transaction_aggregate: { total_transtion: { count: number } }
}> => {
  return httpRequest.get(`/api/account/txs`, {
    params,
  })
}

/**
 * @description 查询账户交易列表-模块地址
 * @param address
 */
export const getAccountModuleTransactionRecord = (
  params: IResParams,
): Promise<{
  ModuleTransfer: IResModuleTransactionRecord[]
  group_count: number
  next_key: string
  offset_key: string
  total_count: number
}> => {
  return httpRequest.get(`/api/account/module_transfer`, {
    params: params,
  })
}

/**
 * @description 账户邀请奖励列表
 * @param address
 */
export const getAccountRewards = ({
  key,
  ...params
}: IResParams): Promise<{
  kyc_invite_transaction: IResAccountRewards[]
  kyc_invite_transaction_aggregate: { aggregate: { count: number } }
  group_count: string
  next_key: string
}> => {
  return httpRequest.post(`/api/account/kyc_invite_rewards_txs`, {
    key,
    ...params,
  })
}

/**
 * @description 账户rollapp交易列表
 * @param address
 */
export const getAccountRollappTransactionRecord = ({
  address,
  limit = 10,
  offset = 0,
}: {
  address: string
  limit?: number
  offset?: number
}): Promise<CheckinRecords_Data> => {
  return httpRequest.get(`/api/rest/account/checkin/records`, {
    params: {
      address,
      limit,
      offset,
    },
  })
}
